import { ScrollArea } from '@/components/ui/scroll-area';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Terminal, ArrowLeft } from 'lucide-react';
import { EDHRecCard } from '@/components/EDHRecCard';
import { ScryfallCard } from '@/types/scryfall';
import { EDHRecCard as EDHRecRecommendation } from '@/hooks/useEDHRec';
import { useState } from 'react';
import { CardDetailsDialog } from './CardDetailsDialog';

interface EDHRecModalProps {
  isOpen: boolean;
  onClose: () => void;
  recommendations: EDHRecRecommendation[] | undefined;
  isLoading: boolean;
  isError: boolean;
  onCardAdded: (card: ScryfallCard) => void;
}

export const EDHRecModal = ({ isOpen, onClose, recommendations, isLoading, isError, onCardAdded }: EDHRecModalProps) => {
  const [selectedCard, setSelectedCard] = useState<ScryfallCard | null>(null);
  const [isCardDetailsOpen, setIsCardDetailsOpen] = useState(false);

  const handleCardClick = (card: ScryfallCard) => {
    setSelectedCard(card);
    setIsCardDetailsOpen(true);
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl">
        <DialogHeader>
          <DialogTitle>EDHREC Recommendations</DialogTitle>
          <DialogDescription>
            Suggestions based on your commander and deck.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <Button onClick={onClose} variant="ghost">
            <ArrowLeft className="mr-2 h-4 w-4" /> Back to Deck
          </Button>

          {isError && (
            <Alert variant="destructive">
              <Terminal className="h-4 w-4" />
              <AlertTitle>Error</AlertTitle>
              <AlertDescription>
                Failed to fetch EDHREC recommendations. Please try again later.
              </AlertDescription>
            </Alert>
          )}

          {recommendations && (
            <ScrollArea className="h-96">
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              {recommendations.map((card) => (
                <EDHRecCard
                  key={card.oracle_id}
                  card={card}
                  onCardAdded={onCardAdded}
                  onCardClick={handleCardClick}
                />
              ))}
            </div>
          </ScrollArea>
          )}

          {isLoading && (
            <ScrollArea className="h-96">
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                {Array.from({ length: 10 }).map((_, i) => (
                  <Skeleton key={i} className="w-full h-64" />
                ))}
              </div>
            </ScrollArea>
          )}
        </div>
      </DialogContent>
      {selectedCard && (
        <CardDetailsDialog
          card={selectedCard}
          open={isCardDetailsOpen}
          onOpenChange={setIsCardDetailsOpen}
          onAddToList={onCardAdded}
        />
      )}
    </Dialog>
  );
};
